import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import { ImageUpload } from '@shared/core-ui-image-upload';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { CustomizationService } from '@api-client/products';
import { dataURLtoBlob } from '@shared/image';
import type { ProductWithHelpers } from '@shared/products-core';
import { useActiveProduct } from '@shared/products-core';
import { getFeatureConfig } from '@luna/feature-config';
import type { Account } from '@shared/products-types';

import { gAPIAccountEntitySlice } from '../gAPIAccountEntitySlice';
import { useActiveProductServices } from '../ActiveProductServicesContext';
import { useActivePAPIProduct } from '../ActivePAPIProductContext';
import { removeImageFromCache, saveImageToCache } from '../services/imageLoadingService';
import { invalidateProductImage } from './hooks/useProductImage';

type Props = {
    dropdownToggle?: ReactNode;
    productType?: string;
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productImageChangeStarted'>>[2];

const IMAGE_PREFIX_LENGTH = 22;

export const ProductImageUpload = forwardRef<HTMLInputElement, Props>(
    ({ dropdownToggle, trigger, productType = '' }, ref) => {
        const product = useActivePAPIProduct();
        const legacyProduct = useActiveProduct<ProductWithHelpers>();
        const {
            id,
            localizedName,
            _embedded: {
                customization: { image },
            },
        } = product;
        const { updateCache } = useActiveProductServices();
        const handleImageUpload = (imageUrl: string) =>
            CustomizationService.postPublicProductsCustomizationImage({
                id,
                formData: { file: dataURLtoBlob(imageUrl) },
            }).then(() => {
                saveImageToCache(id, imageUrl.substr(IMAGE_PREFIX_LENGTH));
                updateCache({
                    ...product,
                    _embedded: {
                        ...product._embedded,
                        customization: {
                            ...product._embedded.customization,
                            image: ['ORIGINAL', 'V250x250'],
                        },
                    },
                });
                if (getFeatureConfig(({ products }) => products.relyOnGAPIForAggregation)) {
                    saveImageToCache(legacyProduct.id, imageUrl.substr(IMAGE_PREFIX_LENGTH));
                    gAPIAccountEntitySlice.updateCache({
                        ...(gAPIAccountEntitySlice.selectById(legacyProduct.id) as Account),
                        hasImage: true,
                    });
                }
            });

        const handleImageCustomizationStart = () =>
            trackEvent2('productCustomisationChanges', 'productImageChangeStarted', {
                trigger,
            });

        const handleImageCustomizationSuccess = () =>
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                productType,
                customisationChanges: ['name'],
            });

        const handleUploadedImageChange = () => {
            georgeStats.trackEvent('accountDetails', 'accountAvatarChanged', {
                productI18N: localizedName,
            });
            handleImageCustomizationSuccess();
        };

        const handleImageRemove = async () => {
            handleImageCustomizationStart();
            return CustomizationService.deletePublicProductsCustomizationImage({ id }).then(() => {
                invalidateProductImage(id);
                updateCache({
                    ...product,
                    _embedded: {
                        ...product._embedded,
                        customization: {
                            ...product._embedded.customization,
                            image: [],
                        },
                    },
                });
                removeImageFromCache(id);
                if (getFeatureConfig(({ products }) => products.relyOnGAPIForAggregation)) {
                    removeImageFromCache(legacyProduct.id);
                    gAPIAccountEntitySlice.updateCache({
                        ...(gAPIAccountEntitySlice.selectById(legacyProduct.id) as Account),
                        hasImage: false,
                    });
                }
                handleImageCustomizationSuccess();
            });
        };

        return (
            <ImageUpload
                ref={ref}
                onUploadedImageChange={handleUploadedImageChange}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                disableRemoveImage={!image?.length}
                showResultAsNotification
                dropdownToggle={dropdownToggle}
                onChangeImageClick={handleImageCustomizationStart}
            />
        );
    },
);
